import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { Header } from '../../components/layout/Header';
import { Footer } from '../../components/layout/Footer';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Eye, EyeSlash, LockKey, CheckCircle, Warning } from '@phosphor-icons/react';
import { API } from '../../utils/api';
import axios from 'axios';

export default function ResetPasswordPage() {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const t = {
    en: {
      title: "Create new password",
      subtitle: "Enter your new password below",
      password: "New password",
      confirmPassword: "Confirm password",
      submit: "Reset password",
      resetting: "Resetting...",
      successTitle: "Password reset!",
      successMessage: "Your password has been reset successfully. You can now log in with your new password.",
      login: "Go to login",
      invalidToken: "Invalid or expired link",
      invalidTokenMessage: "This password reset link is invalid or has expired. Please request a new one.",
      requestNew: "Request new link",
      passwordMismatch: "Passwords don't match",
      passwordTooShort: "Password must be at least 6 characters"
    },
    fr: {
      title: "Créez un nouveau mot de passe",
      subtitle: "Entrez votre nouveau mot de passe ci-dessous",
      password: "Nouveau mot de passe",
      confirmPassword: "Confirmer le mot de passe",
      submit: "Réinitialiser",
      resetting: "Réinitialisation...",
      successTitle: "Mot de passe réinitialisé!",
      successMessage: "Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter.",
      login: "Aller à la connexion",
      invalidToken: "Lien invalide ou expiré",
      invalidTokenMessage: "Ce lien de réinitialisation est invalide ou a expiré. Veuillez en demander un nouveau.",
      requestNew: "Demander un nouveau lien",
      passwordMismatch: "Les mots de passe ne correspondent pas",
      passwordTooShort: "Le mot de passe doit contenir au moins 6 caractères"
    }
  };

  const text = t[language] || t.en;

  // No token provided
  if (!token) {
    return (
      <div className="min-h-screen bg-pp-bg">
        <Header />
        <main className="pt-28 pb-20 md:pt-36 md:pb-28">
          <div className="pp-container">
            <div className="max-w-md mx-auto text-center">
              <div className="w-16 h-16 rounded-full bg-pp-warning-bg mx-auto mb-6 flex items-center justify-center">
                <Warning size={32} className="text-pp-warning-text" weight="fill" />
              </div>
              <h1 className="pp-heading-2 text-pp-text mb-3">{text.invalidToken}</h1>
              <p className="text-pp-text-secondary mb-8">{text.invalidTokenMessage}</p>
              <Link to="/forgot-password" className="pp-btn-primary">
                {text.requestNew}
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError(text.passwordTooShort);
      return;
    }

    if (password !== confirmPassword) {
      setError(text.passwordMismatch);
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.includes('expired') || detail?.includes('Invalid')) {
        setError(text.invalidTokenMessage);
      } else {
        setError(detail || (language === 'fr' ? "Une erreur s'est produite" : "Something went wrong"));
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-pp-bg">
        <Header />
        <main className="pt-28 pb-20 md:pt-36 md:pb-28">
          <div className="pp-container">
            <div className="max-w-md mx-auto text-center">
              <div className="w-16 h-16 rounded-full bg-pp-success-muted mx-auto mb-6 flex items-center justify-center">
                <CheckCircle size={32} className="text-pp-success" weight="fill" />
              </div>
              <h1 className="pp-heading-2 text-pp-text mb-3" data-testid="reset-success">
                {text.successTitle}
              </h1>
              <p className="text-pp-text-secondary mb-8">{text.successMessage}</p>
              <Link to="/login" className="pp-btn-primary">
                {text.login}
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-pp-bg">
      <Header />
      
      <main className="pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="pp-container">
          <div className="max-w-md mx-auto">
            <div className="text-center mb-10">
              <div className="w-16 h-16 rounded-2xl bg-pp-navy/10 mx-auto mb-6 flex items-center justify-center">
                <LockKey size={32} className="text-pp-navy" weight="duotone" />
              </div>
              <h1 className="pp-heading-2 text-pp-text mb-3" data-testid="reset-password-title">
                {text.title}
              </h1>
              <p className="text-pp-text-secondary">{text.subtitle}</p>
            </div>

            <div className="pp-card">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="password" className="pp-label mb-2 block">{text.password}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      className="pp-input pr-12"
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-pp-text-muted hover:text-pp-text"
                    >
                      {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="pp-label mb-2 block">{text.confirmPassword}</Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="pp-input"
                    data-testid="reset-password-confirm"
                  />
                </div>

                {error && (
                  <div className="p-3 rounded-lg bg-pp-error-bg text-pp-error-text text-sm" data-testid="reset-password-error">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !password || !confirmPassword}
                  className="pp-btn-primary w-full disabled:opacity-50"
                  data-testid="reset-password-submit"
                >
                  {loading ? text.resetting : text.submit}
                </button>
              </form>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
